import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Flex,
  Grid,
  GridProps,
  Switch,
  SwitchProps,
  Text,
  TextProps
} from "@chakra-ui/react";
import { CustomModal } from "components/custom-modal";
import { DropdownOptionProps } from "components/dropdown";
import { DropdownField, DropdownFieldProps } from "components/dropdown-field";
import { getOptionNames, onChangeMultiselect } from "components/dropdown/utils";
import { InputField, InputFieldProps } from "components/input-field";
import {
  DraggableFileField,
  DraggableFileFieldProps
} from "components/input-file";
import { CloseIcon } from "components/vectors/close-icon";

import { useEffect, useState } from "react";
import { useEventListener, makeEvent } from "services/events";
import { fieldValidation } from "utils/field-validation";
import { ObjectSchema, objectSchema } from "utils/object-methods";

export type ModalFieldsOptions = {
  onSubmit: (param: { [key: string]: any }) => any;
  onClose?: () => any;
  onReject?: () => any;
  message?:
    | string
    | JSX.Element
    | (({
        onClose
      }: {
        onClose: ModalFieldsOptions["onClose"];
      }) => string | JSX.Element);
  title?: JSX.Element | string;
  confirmButtonStyle?: ButtonProps;
  rejectButtonStyle?: ButtonProps;
  variant?: "normal" | "danger";
  paramsSettings: {
    containerProps?: GridProps;
    params: {
      name: string;
      field: string;
      type?: "input" | "dropdown" | "inputFile" | "switch";
      initialValue?: any;
      fieldProps?: {
        InputField?: Omit<Partial<InputFieldProps>, "value" | "onChange">;
        Box?: BoxProps;
        Switch?: SwitchProps;
        DropdownField?: Partial<DropdownFieldProps> & {
          asyncOptions?: () => Promise<DropdownOptionProps[]>;
        };
        DraggableFileField?: Partial<DraggableFileFieldProps>;
        titleProps?: TextProps;
      };
    }[];
  };
  simpleValidation?: boolean;
  schema?: ObjectSchema;
};

export function ModalFields() {
  const [isLoading, setIsLoading] = useState(false);
  const [params, setParams] = useState<{ [key: string]: any }>({});
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [modalConfirmOptions, setModalConfirmOptions] =
    useState<ModalFieldsOptions | null>(null);
  const [asyncOptions, setAsyncOptions] = useState<{
    [key: string]: DropdownOptionProps[];
  }>({});
  const [isLoadingOptions, setIsLoadingOptions] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    if (modalConfirmOptions) {
      modalConfirmOptions.paramsSettings.params.forEach((param) => {
        if (
          param.type === "dropdown" &&
          param.fieldProps?.DropdownField?.asyncOptions
        ) {
          setIsLoadingOptions((prev) => ({ ...prev, [param.field]: true }));
          param.fieldProps.DropdownField.asyncOptions()
            .then((options) => {
              if (options) {
                setAsyncOptions((prev) => ({
                  ...prev,
                  [param.field]: options as DropdownOptionProps[]
                }));
              }
            })
            .finally(() => {
              setIsLoadingOptions((prev) => ({
                ...prev,
                [param.field]: false
              }));
            });
        }
      });
    }
  }, [modalConfirmOptions]);

  function onOpen(data: ModalFieldsOptions) {
    setModalConfirmOptions(data);
    setParams(
      data.paramsSettings.params.reduce(
        (acc, curr) => {
          acc[curr.field] =
            curr.type === "switch"
              ? (curr.initialValue ?? false)
              : (curr.initialValue ?? "");
          return acc;
        },
        {} as { [key: string]: any }
      )
    );
  }

  function onClose() {
    modalConfirmOptions?.onClose?.();
    setModalConfirmOptions(null);
    setErrors({});
    setParams({});
    setIsLoading(false);
  }

  const { onClick: rejectButtonOnClick, ...rejectButtonStyle } =
    modalConfirmOptions?.rejectButtonStyle || {};
  const { onClick: confirmButtonOnClick, ...confirmButtonStyle } =
    modalConfirmOptions?.confirmButtonStyle || {};

  const simpleValidation = (body: any) => {
    const schema = {
      ...modalConfirmOptions!.paramsSettings.params.reduce((acc, curr) => {
        if (curr.type === "switch") return acc;
        acc[curr.field] = (value: any) => {
          const { isValid, errorMessage } = fieldValidation({ value }).required(
            { message: "Preecha o campo" }
          );
          return { valid: isValid, message: errorMessage };
        };
        return acc;
      }, {} as ObjectSchema)
    };
    return objectSchema(schema, body);
  };

  const onChangeField = (field: string, value: any) => {
    setParams((prev) => ({ ...prev, [field]: value }));
    setErrors((e) => {
      delete e[field];
      return { ...e };
    });
  };

  const modalFooter = modalConfirmOptions ? (
    <>
      <Button
        variant={modalConfirmOptions.variant}
        onClick={async (e) => {
          let validation: { isValid: boolean; body: any; errors: any } | null =
            null;

          if (modalConfirmOptions.schema) {
            validation = objectSchema(modalConfirmOptions.schema, params);
          } else if (modalConfirmOptions.simpleValidation) {
            validation = simpleValidation(params);
          }

          if (validation && !validation.isValid) {
            return setErrors(validation.errors);
          }
          setIsLoading(true);
          try {
            await modalConfirmOptions.onSubmit(params);
          } catch {
          } finally {
            setIsLoading(false);
            onClose();
            confirmButtonOnClick?.(e);
          }
        }}
        isLoading={isLoading}
        children="Salvar"
        {...confirmButtonStyle}
      />{" "}
      <Button
        variant="outline"
        onClick={(e) => {
          modalConfirmOptions.onReject?.();
          onClose();
          rejectButtonOnClick?.(e);
        }}
        leftIcon={<CloseIcon />}
        children="Cancelar"
        isDisabled={isLoading}
        {...rejectButtonStyle}
      />
    </>
  ) : undefined;

  useEventListener("openModalFields", (data: ModalFieldsOptions) => {
    if (modalConfirmOptions !== null) {
      new Error("The confirmation modal is already open");
    } else onOpen(data);
  });

  return (
    <>
      <CustomModal
        isOpen={modalConfirmOptions !== null}
        onClose={onClose}
        modalFooter={modalFooter}
        modalTitle={modalConfirmOptions?.title ?? ""}
        size="md"
      >
        <Flex flexDir="column" alignItems="center" gap="10px">
          {typeof modalConfirmOptions?.message === "function"
            ? modalConfirmOptions.message({ onClose })
            : modalConfirmOptions?.message
              ? modalConfirmOptions.message
              : null}

          {modalConfirmOptions?.paramsSettings ? (
            <Grid
              w="100%"
              gap="12px"
              {...modalConfirmOptions?.paramsSettings.containerProps}
            >
              {modalConfirmOptions.paramsSettings.params.map(
                ({ fieldProps, name, field, type = "input" }, i) => {
                  if (type === "input") {
                    return (
                      <Box key={i} {...fieldProps?.Box}>
                        <Text
                          mb="8px"
                          fontWeight="medium"
                          {...fieldProps?.titleProps}
                        >
                          {name}
                        </Text>
                        <InputField
                          {...fieldProps?.InputField}
                          value={params[field]}
                          onChange={(e) => onChangeField(field, e.target.value)}
                          errorMessage={errors[field]}
                        />
                      </Box>
                    );
                  } else if (type === "dropdown") {
                    return (
                      <Box key={i} {...fieldProps?.Box}>
                        <Text
                          mb="8px"
                          fontWeight="medium"
                          {...fieldProps?.titleProps}
                        >
                          {name}
                        </Text>
                        <DropdownField
                          {...fieldProps?.DropdownField}
                          value={
                            fieldProps?.DropdownField?.dropdownProps
                              ?.multiSelect
                              ? params[field] || []
                              : params[field]
                          }
                          onChange={(value) =>
                            onChangeField(
                              field,
                              fieldProps?.DropdownField?.dropdownProps
                                ?.multiSelect
                                ? onChangeMultiselect(value, params[field])
                                : value
                            )
                          }
                          errorMessage={errors[field]}
                          isLoading={isLoadingOptions[field]}
                          options={
                            asyncOptions[field] ||
                            fieldProps?.DropdownField?.dropdownProps?.options ||
                            []
                          }
                          dropdownProps={{
                            children: getOptionNames(
                              params[field],
                              asyncOptions[field] ||
                                fieldProps?.DropdownField?.dropdownProps
                                  ?.options ||
                                []
                            ),
                            ...fieldProps?.DropdownField?.dropdownProps
                          }}
                        />
                      </Box>
                    );
                  } else if (type === "inputFile") {
                    return (
                      <Box key={i} {...fieldProps?.Box}>
                        <Text
                          mb="8px"
                          fontWeight="medium"
                          {...fieldProps?.titleProps}
                        >
                          {name}
                        </Text>
                        <DraggableFileField
                          fileExtensions={[]}
                          {...fieldProps?.DraggableFileField}
                          file={params[field]}
                          handleDelete={() => onChangeField(field, undefined)}
                          handleFile={(file) => onChangeField(field, file)}
                          error={errors[field] || ""}
                        />
                      </Box>
                    );
                  } else if (type === "switch") {
                    return (
                      <Box key={i} {...fieldProps?.Box}>
                        <Text
                          mb="8px"
                          fontWeight="medium"
                          {...fieldProps?.titleProps}
                        >
                          {name}
                        </Text>
                        <Switch
                          {...fieldProps?.Switch}
                          isChecked={params[field]}
                          onChange={(e) =>
                            onChangeField(field, e.target.checked)
                          }
                        />
                      </Box>
                    );
                  }
                  return null;
                }
              )}
            </Grid>
          ) : null}
        </Flex>
      </CustomModal>
    </>
  );
}

export function openModalFields(options: ModalFieldsOptions) {
  makeEvent("openModalFields", options);
}
